import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import './App.css';
import './Services.css';

function Services() {
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [services, setServices] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  const CONSULTATION_FEE = 200;
  
  // States to track database operations
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  // Fetch services data
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        // Using the correct API endpoint as provided
        const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/services', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        // Ensure data is an array before setting it
        if (Array.isArray(data)) {
          // Updating the key names to match the actual response data
          const formattedData = data.map(service => ({
            Service_ID: service.Service_Id,  // Note: API returns Service_Id (lowercase 'd')
            Service_Name: service.Service_Name,
            Description: service.Description,
            Is_Active: service.Is_Active
          }));
          setServices(formattedData);
        } else {
          throw new Error('Received data is not an array');
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch services:", error);
        setError(error.message);
        setLoading(false);
        // Set services to empty array when there's an error
        setServices([]);
      }
    };

    fetchServices();
  }, []);

  // Fetch prices data
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/prices', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          setPrices(data);
        } else {
          throw new Error('Received price data is not an array');
        }
      } catch (error) {
        console.error("Failed to fetch prices:", error);
        // Use fallback prices if API fails
        setPrices([]);
      }
    };

    fetchPrices();
  }, []);

  // Calculate total amount when selected services change
  useEffect(() => {
    if (selectedServices.length > 0 && prices.length > 0) {
      let total = CONSULTATION_FEE;
      const breakdown = [{ service: "Consultation Fee", price: CONSULTATION_FEE }];
      
      selectedServices.forEach(serviceId => {
        const priceItem = prices.find(price => price.Service_Id && price.Service_Id.toString() === serviceId);
        const serviceItem = services.find(service => service.Service_ID && service.Service_ID.toString() === serviceId);
        
        if (priceItem && serviceItem) {
          total += priceItem.Price;
          breakdown.push({
            service: serviceItem.Service_Name,
            price: priceItem.Price
          });
        } else {
          console.warn(`No price found for service ID: ${serviceId}`);
        }
      });
      
      setTotalAmount(total);
      setPriceBreakdown(breakdown);
    } else {
      // If no services selected, just show consultation fee
      setTotalAmount(CONSULTATION_FEE);
      setPriceBreakdown([{ service: "Consultation Fee", price: CONSULTATION_FEE }]);
    }
  }, [selectedServices, prices, services]);

  // Add a fallback for when the API fails
  useEffect(() => {
    // If there's an error or loading takes too long, use fallback data
    const timeoutId = setTimeout(() => {
      if (loading && services.length === 0) {
        console.log("Using fallback service data");
        // Fallback data in case API fails - using the correct structure
        const fallbackServices = [
          { Service_ID: 1, Service_Name: "Removal of stitches - post operation" },
          { Service_ID: 2, Service_Name: "Diagnostic Service @ Home" },
          { Service_ID: 3, Service_Name: "Dressings" },
          { Service_ID: 4, Service_Name: "Injections and Infusions" },
          { Service_ID: 5, Service_Name: "Physiotherapy" },
          { Service_ID: 6, Service_Name: "Medical Checkup at Home" }
        ];
        setServices(fallbackServices);
        setLoading(false);
      }
    }, 5000); // 5 seconds timeout

    return () => clearTimeout(timeoutId);
  }, [loading, services]);

  // Also add fallback prices if needed
  useEffect(() => {
    if (prices.length === 0 && services.length > 0) {
      console.log("Using fallback price data");
      const fallbackPrices = [
        { Service_Id: 1, Price: 300, Price_Id: 1 },
        { Service_Id: 2, Price: 500, Price_Id: 2 },
        { Service_Id: 3, Price: 200, Price_Id: 3 },
        { Service_Id: 4, Price: 250, Price_Id: 4 },
        { Service_Id: 5, Price: 400, Price_Id: 5 },
        { Service_Id: 6, Price: 350, Price_Id: 6 }
      ];
      setPrices(fallbackPrices);
    }
  }, [prices, services]);

  const getMinDate = () => {
    const today = new Date();
    return adjustForWeekends(today).toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(today.setDate(today.getDate() + 4));
    return adjustForWeekends(maxDate).toISOString().split('T')[0];
  };

  // Adjusts the date to skip Saturdays and Sundays
  const adjustForWeekends = (date) => {
    const day = date.getDay();
    if (day === 6) date.setDate(date.getDate() + 2); // Saturday -> Move to Monday
    else if (day === 0) date.setDate(date.getDate() + 1); // Sunday -> Move to Monday
    return date;
  };

  const blockWeekends = (e) => {
    const selectedDate = new Date(e.target.value);
    const day = selectedDate.getDay();

    // Clear and alert user if a weekend is selected
    if (day === 6 || day === 0) {
      e.target.value = ''; // Clear invalid date
      alert('Weekends are not allowed! Please select a weekday.');
    }
  };

  const validateDate = (e) => {
    blockWeekends(e);
  };
    
  // State to manage form inputs
  const [formData, setFormData] = useState({
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    State: '',
    Pincode: '',
    AddressConfirmed: false
  });
  
  // State to manage booking form inputs
  const [bookingFormData, setBookingFormData] = useState({
    Name: '',
    Number: '',
    Date: '',
    Gender: '',
    Message: '',
    TermsAgreed: false
  });

  // Handle booking form input changes
  const handleBookingFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBookingFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // State to manage validation errors
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Address Line 1 validation
    if (!formData.AddressLine1.trim()) {
      newErrors.AddressLine1 = 'Address Line 1 is required';
    } else if (formData.AddressLine1.length < 5) {
      newErrors.AddressLine1 = 'Address must be at least 5 characters long';
    }

    // City validation
    if (!formData.City.trim()) {
      newErrors.City = 'City is required';
    } else if (!/^[A-Za-z\s]{3,}$/.test(formData.City)) {
      newErrors.City = 'City must contain only letters and be at least 3 characters long';
    }

    // State validation
    if (!formData.State.trim()) {
      newErrors.State = 'State is required';
    } else if (!/^[A-Za-z\s]{3,}$/.test(formData.State)) {
      newErrors.State = 'State must contain only letters and be at least 3 characters long';
    }

    // Pincode validation
    if (!formData.Pincode.trim()) {
      newErrors.Pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.Pincode)) {
      newErrors.Pincode = 'Pincode must be exactly 6 digits';
    }

    // Address Confirmation validation
    if (!formData.AddressConfirmed) {
      newErrors.AddressConfirmed = 'Please confirm your address';
    }

    return newErrors;
  };

  // Split name into first, middle, and last name
  const splitName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') {
      return { firstName: 'Guest', middleName: '', lastName: 'User' };
    }
    
    const trimmedName = fullName.trim();
    if (!trimmedName) {
      return { firstName: 'Guest', middleName: '', lastName: 'User' };
    }
    
    const nameParts = trimmedName.split(/\s+/);
    
    let firstName = '';
    let middleName = '';
    let lastName = '';
    
    if (nameParts.length === 1) {
      firstName = nameParts[0];
      lastName = '';
    } else if (nameParts.length === 2) {
      firstName = nameParts[0];
      lastName = nameParts[1];
    } else {
      firstName = nameParts[0];
      lastName = nameParts[nameParts.length - 1];
      middleName = nameParts.slice(1, nameParts.length - 1).join(' ');
    }
    
    // Make sure we have some values
    firstName = firstName || 'Guest';
    lastName = lastName || 'User';
    
    return { firstName, middleName, lastName };
  };

  // Function to check if patient exists and create if not
const getOrCreatePatient = async (patientData) => {
  try {
    // Debug log to check the incoming patient data
    console.log('Checking for patient with data:', patientData);
    
    // Check if patient exists by phone number
    const checkResponse = await fetch(`https://hospitalbackend-446302.el.r.appspot.com/patients?phone=${encodeURIComponent(patientData.Phone_Number)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
    });
    
    if (!checkResponse.ok) {
      console.warn('Error checking for existing patient, proceeding to create new one');
    } else {
      const existingPatients = await checkResponse.json();
      if (Array.isArray(existingPatients) && existingPatients.length > 0) {
        const existingPatient = existingPatients[0];
        console.log('Found existing patient by phone number', existingPatient);
        
        // Compare existing patient data with current form data
        const hasChanges = 
          existingPatient.F_Name !== patientData.F_Name ||
          existingPatient.L_Name !== patientData.L_Name ||
          existingPatient.Gender_Cd !== patientData.Gender_Cd ||
          existingPatient.Address !== patientData.Address ||
          existingPatient.City !== patientData.City ||
          existingPatient.State !== patientData.State ||
          existingPatient.Pincode !== patientData.Pincode;
        
        // If there are differences, update the patient record
        if (hasChanges) {
          console.log('Patient data has changed. Updating record...');
          try {
            // Update patient record if it exists but has different data
            const updateResponse = await fetch(`https://hospitalbackend-446302.el.r.appspot.com/patients/${existingPatient.Patient_Id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...existingPatient,
                F_Name: patientData.F_Name,
                L_Name: patientData.L_Name,
                M_Name: patientData.M_Name,
                Gender_Cd: patientData.Gender_Cd,
                Address: patientData.Address,
                City: patientData.City,
                State: patientData.State,
                Pincode: patientData.Pincode
              })
            });
            
            if (!updateResponse.ok) {
              console.warn('Failed to update patient record, continuing with existing record');
            } else {
              console.log('Patient record updated successfully');
            }
          } catch (updateError) {
            console.error('Error updating patient:', updateError);
            // Continue with existing record if update fails
          }
        }
        
        return existingPatient.Patient_Id;
      }
    }
    
    // If no matching patient found, create a new one
    console.log('Creating new patient with data:', patientData);
    const createResponse = await fetch('https://hospitalbackend-446302.el.r.appspot.com/patients', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patientData)
    });
    
    if (!createResponse.ok) {
      throw new Error(`Failed to create patient: ${createResponse.status}`);
    }
    
    const newPatient = await createResponse.json();
    console.log('Created new patient', newPatient);
    
    // Return the newly created patient ID
    return newPatient.Patient_Id;
  } catch (error) {
    console.error('Error in getOrCreatePatient:', error);
    throw error;
  }
};

  // Function to create a booking
  const createBooking = async (bookingData) => {
  try {
    // Ensure all required fields are present and not null
    const formattedBookingData = { ...bookingData };
    
    // Format the dates correctly
    if (formattedBookingData.Booking_Date_Time) {
      const bookingDate = new Date(formattedBookingData.Booking_Date_Time);
      formattedBookingData.Booking_Date_Time = bookingDate.toISOString().replace('T', ' ').slice(0, 19);
    }
    
    if (formattedBookingData.Preferred_Date) {
      const preferredDate = new Date(formattedBookingData.Preferred_Date);
      formattedBookingData.Preferred_Date = preferredDate.toISOString().slice(0, 10);
    }
    
    if (formattedBookingData.Update_Dt_Tm) {
      const updateDate = new Date(formattedBookingData.Update_Dt_Tm);
      formattedBookingData.Update_Dt_Tm = updateDate.toISOString().replace('T', ' ').slice(0, 19);
    }
    
    // IMPORTANT FIX: Ensure Booking_Confirmed_Time_Slot is not null
    // Use current time + 1 hour as default time slot if null
    if (formattedBookingData.Booking_Confirmed_Time_Slot === null) {
      const timeSlot = new Date();
      timeSlot.setHours(timeSlot.getHours() + 1);
      formattedBookingData.Booking_Confirmed_Time_Slot = timeSlot.toISOString().replace('T', ' ').slice(0, 19);
    }
    
    // Log what we're sending to help debug
    console.log('Sending booking data to API:', JSON.stringify(formattedBookingData, null, 2));
    
    const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formattedBookingData)
    });
    
    // Get more details about the error if possible
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to create booking: ${response.status}. Details:`, errorText);
      throw new Error(`Failed to create booking: ${response.status}. Details: ${errorText}`);
    }
    
    const newBooking = await response.json();
    console.log('Created new booking', newBooking);
    
    return newBooking.Booking_Id;
  } catch (error) {
    console.error('Error in createBooking:', error);
    throw error;
  }
};  
  // Function to create booking services
  const createBookingService = async (bookingServiceData) => {
    try {
      const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/booking-services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingServiceData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create booking service: ${response.status}`);
      }
      
      const newBookingService = await response.json();
      console.log('Created new booking service', newBookingService);
      
      return newBookingService;
    } catch (error) {
      console.error('Error in createBookingService:', error);
      throw error;
    }
  };

  // Handle address form submission with database operations
const handleAddressSubmit = async (e) => {
  e.preventDefault();
  
  // Validate form
  const validationErrors = validateForm();
  
  // If there are errors, set them and prevent submission
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  // Set submitting state
  setIsSubmitting(true);
  setSubmissionError(null);
  
  try {
    // 1. Process name to get first, middle, and last names
    let { firstName, middleName, lastName } = splitName(bookingFormData.Name);
    
    // Make sure we have values for all names
    if (!firstName.trim()) firstName = "Guest";
    if (!lastName.trim()) lastName = "User";
    
    console.log(`Parsed name: First=${firstName}, Middle=${middleName}, Last=${lastName}`);
    
    // 2. Create patient data
    const patientData = {
      F_Name: firstName,
      M_Name: middleName,
      L_Name: lastName,
      Email: '',  // Leave empty as per requirements
      Phone_Number: bookingFormData.Number,
      Date_Of_Birth: null,
      Gender_Cd: bookingFormData.Gender === 'Male' ? 100 : 101,
      Medical_History_Id: null,
      Address: `${formData.AddressLine1} ${formData.AddressLine2}`.trim(),
      City: formData.City,
      State: formData.State,
      Pincode: parseInt(formData.Pincode, 10),
      Adhar_Number: ''
    };
    
    // 3. Check if patient exists or create new one
    const patientId = await getOrCreatePatient(patientData);
    
    // 4. Create booking data with all required fields
    const currentDate = new Date();
    let formattedDate = new Date();
    
    // Make sure we have a valid date from the form
    if (bookingFormData.Date) {
      try {
        formattedDate = new Date(bookingFormData.Date);
        if (isNaN(formattedDate.getTime())) {
          console.warn("Invalid date from form, using current date instead");
          formattedDate = new Date();
        }
      } catch (error) {
        console.warn("Error parsing date, using current date instead:", error);
        formattedDate = new Date();
      }
    }
    
    // Set booking time slot (1 hour from current time)
    const timeSlot = new Date();
    timeSlot.setHours(timeSlot.getHours() + 1);
    
    // Format dates properly for the API
    const bookingData = {
      Patient_Id: patientId,
      Booking_Date_Time: currentDate.toISOString().replace('T', ' ').slice(0, 19),
      Booking_Status_Cd: 106,
      Booking_Confirmed_Time_Slot: timeSlot.toISOString().replace('T', ' ').slice(0, 19), // IMPORTANT: Not null
      Preferred_Date: formattedDate.toISOString().slice(0, 10),
      Total_Price: totalAmount,
      Update_Dt_Tm: currentDate.toISOString().replace('T', ' ').slice(0, 19)
    };
    
    // 5. Create booking record
    const bookingId = await createBooking(bookingData);
    
    // 6. Create booking services records
    const bookingServicePromises = selectedServices.map(async (serviceId) => {
      const serviceObject = services.find(s => s.Service_ID && s.Service_ID.toString() === serviceId);
      const priceObject = prices.find(p => p.Service_Id && p.Service_Id.toString() === serviceId);
      
      if (!serviceObject || !priceObject) {
        console.warn(`Missing service or price info for service ID: ${serviceId}`);
        return null;
      }
      
      const bookingServiceData = {
        Booking_Id: bookingId,
        Service_Id: parseInt(serviceId, 10),
        Service_Price_Cd: priceObject.Price_Id || null,
        Assigned_Staff_Id: null,
        Booking_Service_Status_Cd: 106,
        Update_Dt_Tm: currentDate.toISOString().replace('T', ' ').slice(0, 19),
        Status_Comments: "booking created"
      };
      
      return createBookingService(bookingServiceData);
    });
    
    // Wait for all booking services to be created
    await Promise.all(bookingServicePromises.filter(p => p !== null));
    
    console.log('All records created successfully');
    setSubmissionSuccess(true);
    
    // Reset form data and errors
    setFormData({
      AddressLine1: '',
      AddressLine2: '',
      City: '',
      State: '',
      Pincode: '',
      AddressConfirmed: false
    });
    setBookingFormData({
      Name: '',
      Number: '',
      Date: '',
      Gender: '',
      Message: '',
      TermsAgreed: false
    });
    setErrors({});
    setSelectedServices([]);
    
    // Close popup after successful submission
    closePopup();
    
    // Show success message
    alert('Booking confirmed successfully! Your booking has been registered.');
    
  } catch (error) {
    console.error('Submission error', error);
    setSubmissionError(error.message || 'An error occurred while processing your booking');
  } finally {
    setIsSubmitting(false);
  }
};
  
  // Updated toggleCheckboxes function to work with substring matching
  const toggleCheckboxes = (searchTerm) => {
    // Only proceed if services is an array and has data
    if (!Array.isArray(services) || services.length === 0) {
      console.warn('No services available to toggle');
      return;
    }

    // Find all services that contain the search term as a substring
    const matchingServices = services
      .filter(service => service && service.Service_Name && service.Service_Name.toLowerCase().includes(searchTerm.toLowerCase()))
      .map(service => service.Service_ID.toString());

    if (matchingServices.length === 0) {
      console.warn(`No services found with the search term: ${searchTerm}`);
      return;
    }

    const updatedServices = new Set(selectedServices);

    matchingServices.forEach(serviceId => {
      const checkbox = document.getElementById(serviceId);
      if (checkbox) {
        if (updatedServices.has(serviceId)) {
          updatedServices.delete(serviceId);
          checkbox.checked = false;
        } else {
          updatedServices.add(serviceId);
          checkbox.checked = true;
        }
      }
    });

    setSelectedServices(Array.from(updatedServices));
    
    // Scroll to the booking section
    const bookSection = document.querySelector('.book, #book');
    if (bookSection) {
      bookSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle service selection
  const handleServiceSelection = (e, serviceId) => {
    if (e.target.checked) {
      setSelectedServices([...selectedServices, serviceId]);
    } else {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    }
  };

  // Define service categories with their corresponding search terms and icons
  const serviceCategories = [
    { title: "Medical check-up", icon: "📋", searchTerm: "Medical Checkup" },
    { title: "Dressings", icon: "🩹", searchTerm: "Dressings" },
    { title: "Injections and infusions", icon: "💉", searchTerm: "Injections" },
    { title: "Telemedicine consult with a Doctor", icon: "📱", searchTerm: "Telemedicine" },
    { title: "Supportive Care", icon: "🤝", searchTerm: "Supportive" },
    { title: "ECG", icon: "❤️", searchTerm: "ECG" },
    { title: "Diagnostic Services", icon: "🏠", searchTerm: "Diagnostic" },
    { title: "Physiotherapy", icon: "💪", searchTerm: "Physio" },
    { title: "Post-operative care", icon: "👨‍⚕️", searchTerm: "stitches" },
    { title: "Lab Collection", icon: "🧪", searchTerm: "Laboratory" }
  ];

  // Existing useEffect and other methods
  useEffect(() => {
    const handleScroll = () => {
      setIsMenuActive(false);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuActive(!isMenuActive);
  };

  const openPopup = () => {
    setIsPopupVisible(true);
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setSubmissionError(null);
    setSubmissionSuccess(false);
  };

  return (
    <div className="services-page">
      <header className="header">
        <Link to="/" className="logo">
          <FontAwesomeIcon icon={faHeartbeat} /> Hospital
        </Link>
        <nav className={`navbar ${isMenuActive ? 'active' : ''}`}>
          <Link to="/">home</Link>
          <a href="#services">services</a>
          <a href="#about">about</a>
          <a href="#doctors">doctors</a>
          <a href="#book">book</a>
          <a href="#review">review</a>
          <a href="#Payments">Payments</a>
        </nav>

        <button
          id="menu-btn"
          className={isMenuActive ? 'fa-times' : ''}
          onClick={toggleMenu}
        >
          <FontAwesomeIcon icon={isMenuActive ? faTimes : faHeartbeat} />
        </button>
      </header>
      <br />

      <div className="containersection">
        <div className="medical-services-wrapper">
          <div className="medical-services-container">
            <a href="#book">
              <button id="SER-btn" className="btn">
                Book Service<span className="fas fa-chevron-right"> </span>
              </button>
            </a>

            <Link to={{
              pathname: "/BookingTracker",
              state: { phoneNumber: bookingFormData.Number }
            }}>
            
              <button id="SER-btn" className="btn">
                BookingTracker<span className="fas fa-chevron-right"> </span>
              </button>Patient
            </Link>
            
            {/* Dynamic service category buttons */}
            {serviceCategories.map((category, index) => (
              <button 
                key={index}
                className="medical-service-button"
                onClick={() => toggleCheckboxes(category.searchTerm)}
              >
                <div className="medical-service-icon">{category.icon}</div>
                <p className="medical-service-title">{category.title}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="book" id="book">
        <h1 className="heading">
          <span>Book a service</span> now.
        </h1>
        <div className="row">
          <div className="image">
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQe8EVWGYBOy5hACG9G_sOzB1M4AkkVanaQbw&usqp=CAU"
              alt="Medical service"
            />
          </div>
          <form
            name="google-sheet-form"
            method="post"
            onSubmit={(e) => {
              e.preventDefault();
              openPopup();
            }}
          >
            <input
              type="text"
              name="Name"
              placeholder="Your name"
              className="box"
              required
              value={bookingFormData.Name}
              onChange={handleBookingFormChange}
            />
            <input
              type="tel"
              name="Number"
              placeholder="Your phone number"
              className="box"
              required
              maxLength={10}
              value={bookingFormData.Number}
              onChange={handleBookingFormChange}
            />
            <input
              type="date"
              name="Date"
              className="box"
              required
              min={getMinDate()}
              max={getMaxDate()}
              onChange={(e) => {
                validateDate(e);
                handleBookingFormChange(e);
              }}
              onFocus={(e) => e.target.addEventListener('input', blockWeekends)}
              value={bookingFormData.Date}
            />         
            <select 
              name="Gender" 
              id="gender" 
              className="box" 
              required
              value={bookingFormData.Gender}
              onChange={handleBookingFormChange}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            
            {/* Improved Dynamic Checkboxes section with better error handling */}
            <div className="checkbox-grid">
              {loading ? (
                <p>Loading services...</p>
              ) : error ? (
                <div>
                  <p>Error loading services. Please try again later.</p>
                  <p style={{ fontSize: '12px', color: '#666' }}>
                    Technical details: {error}
                  </p>
                </div>
              ) : services.length === 0 ? (
                <p>No services available. Please try again later.</p>
              ) : (
                services.map(service => {
                  // Add null/undefined check for service and service.Service_ID
                  if (!service || service.Service_ID === undefined) {
                    console.debug('Skipping invalid service:', service);
                    return null;
                  }
                  
                  return (
                    <div className="checkbox-item" key={service.Service_ID}>
                      <input 
                        type="checkbox" 
                        id={service.Service_ID.toString()} 
                        name="Service" 
                        value={service.Service_ID.toString()}
                        onChange={(e) => handleServiceSelection(e, e.target.value)}
                      />
                      <label htmlFor={service.Service_ID.toString()}>
                        {service.Service_Name || 'Unnamed Service'}
                      </label>
                    </div>
                  );
                })
              )}
            </div>

            <textarea
              name="Message"
              rows="3"
              placeholder="Any additional information or special requests"
              className="box"
              maxLength={200}
              value={bookingFormData.Message}
              onChange={handleBookingFormChange}
            ></textarea>
            <label>
              <input 
                type="checkbox" 
                name="TermsAgreed" 
                required
                checked={bookingFormData.TermsAgreed}
                onChange={handleBookingFormChange}
              />
              <a href="/terms-and-conditions" className="taandc" target="_blank" rel="noopener noreferrer" >  <h1 className="taandc">I agree to the terms and conditions</h1></a> 
            </label>
            <input type="submit" value="Book Now" className="btn" />
          </form>
        </div>
      </section>

      {isPopupVisible && (
        <div className="popup-overlay">
          <div className="popup-container">
            <button className="close-btn" onClick={closePopup}>
              &times;
            </button>
            <h2 className="checkbox-headline">Please Provide Your Address</h2>
            <form id="addressForm" onSubmit={handleAddressSubmit}>
              <div>
                <input
                  type="text"
                  name="AddressLine1"
                  placeholder="Address Line 1"
                  className={`box ${errors.AddressLine1 ? 'error-input' : ''}`}
                  value={formData.AddressLine1}
                  onChange={handleChange}
                />
                {errors.AddressLine1 && <span className="error-message">{errors.AddressLine1}</span>}
              </div>

              <input
                type="text"
                name="AddressLine2"
                placeholder="Address Line 2"
                className="box"
                value={formData.AddressLine2}
                onChange={handleChange}
                pattern="^[A-Za-z ]*$"
                title="Only alphabets and spaces are allowed"
              />

              <div>
                <input
                  type="text"
                  name="City"
                  placeholder="City"
                  className={`box ${errors.City ? 'error-input' : ''}`}
                  value={formData.City}
                  onChange={handleChange}
                  pattern="^[A-Za-z ]*$"
                  title="Only alphabets and spaces are allowed"
                />
                {errors.City && <span className="error-message">{errors.City}</span>}
              </div>

              <div>
                <input
                  type="text"
                  name="State"
                  placeholder="State"
                  className={`box ${errors.State ? 'error-input' : ''}`}
                  value={formData.State}
                  onChange={handleChange}
                  pattern="^[A-Za-z ]*$"
                  title="Only alphabets and spaces are allowed"
                />
                {errors.State && <span className="error-message">{errors.State}</span>}
              </div>

              <div>
                <input
                  type="text"
                  name="Pincode"
                  placeholder="Pincode"
                  className={`box ${errors.Pincode ? 'error-input' : ''}`}
                  maxLength="6"
                  value={formData.Pincode}
                  onChange={handleChange}
                />
                {errors.Pincode && <span className="error-message">{errors.Pincode}</span>}
              </div>

              <label>
                <input
                  type="checkbox"
                  name="AddressConfirmed"
                  checked={formData.AddressConfirmed}
                  onChange={handleChange}
                />
                <span className="taandc"> I confirm the address is correct</span>
                {errors.AddressConfirmed && <span className="error-message">{errors.AddressConfirmed}</span>}
              </label>

              {/* Updated Price Breakdown Section with better styling */}
              <div className="total-amount">
                <h3>Price Breakdown:</h3>
                <div className="price-breakdown">
                  {priceBreakdown.map((item, index) => (
                    <div key={index} className="breakdown-item">
                      <span className="service-name">{item.service}</span>
                      <span className="service-price">₹{item.price || 0}</span>
                    </div>
                  ))}
                  <div className="breakdown-item total">
                    <span className="service-name">Total Amount:</span>
                    <span className="service-price">₹{isNaN(totalAmount) ? 0 : totalAmount}</span>
                  </div>
                </div>
              </div>

              <div className="form-buttons">
                <input type="submit" value="Confirm Booking" className="btn" />
                <button type="button" className="btn cancel-btn" onClick={closePopup}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Add these styles to fix the price breakdown display */
        .price-breakdown {
          margin-top: 10px;
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        
        .breakdown-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e9ecef;
          color: #333;
          font-size: 14px;
        }
        
        .breakdown-item:last-child {
          border-bottom: none;
        }
        
        .breakdown-item.total {
          font-weight: bold;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 2px solid #16a085;
          border-bottom: none;
          font-size: 16px;
          color: #16a085;
        }
        
        .service-name {
          max-width: 70%;
        }
        
        .service-price {
          font-weight: 600;
        }
        
        .total-amount h3 {
          color: #333;
          margin-bottom: 10px;
          font-size: 18px;
        }
        
        /* Error message styling */
        .error-message {
          color: #e74c3c;
          font-size: 12px;
          margin-top: 4px;
          display: block;
        }
        
        /* Ensure text in popup is visible */
        .popup-container {
          background-color: white;
          color: #333;
          padding: 25px;
          border-radius: 10px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }
        
        .checkbox-headline {
          color: #16a085;
          margin-bottom: 20px;
          text-align: center;
          font-size: 24px;
        }
        
        .taandc {
          color: #555;
          font-size: 14px;
          font-weight: normal;
        }
        
        /* Make form inputs more modern */
        .box {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          margin-bottom: 15px;
          font-size: 14px;
          transition: border-color 0.3s;
        }
        
        .box:focus {
          border-color: #16a085;
          outline: none;
          box-shadow: 0 0 0 3px rgba(22, 160, 133, 0.1);
        }
        
        .error-input {
          border-color: #e74c3c;
        }
        

  
      `}</style>
    </div>
  );
}

export default Services;